// Maps negative song list IDs (brand-new song lists) to server assigned song list IDs, and a church Sunday's retired
// server ID to the row the server saved it under (issue #4190).
// A convenience for the open EventEditor, to map the ID of a brand-new event after it successfully uploads to the
// server. Cleared when the event calendar closes and lost on a reload, so correctness does not rest on it: the upload
// rewrites a reassigned ID in the pending update queue and in the cached song lists (replaceSongListIds in
// use_current_org.tsx); the upload and the planner's writers (useAllSongLists) map IDs through here only for a write
// made from a render that still showed the old one.
import { useSyncExternalStore } from 'react';
import { SongListId } from '../../../common/src/model.ts';

const songListIdMap = new Map<SongListId, SongListId>();
const listeners: Array<() => void> = [];

export function clearSongListIdMap() {
  songListIdMap.clear();
  notifyListeners();
}

export function addToSongListIdMap(rawId: SongListId, id: SongListId) {
  songListIdMap.set(rawId, id);
  notifyListeners();
}

function notifyListeners() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribeToSongListIdMap(callback: () => void) {
  listeners.push(callback);
  return () => {
    const index = listeners.indexOf(callback);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };
}

export function useMappedSongListId({ rawId }: { rawId: SongListId }): SongListId {
  return useSyncExternalStore(subscribeToSongListIdMap, () => mapSongListId(rawId));
}

export function mapSongListId(rawId: SongListId): SongListId {
  return songListIdMap.get(rawId) ?? rawId;
}
